// client/src/components/pages/WeighbridgeCompliance/DocumentManager.tsx
// (TypeScript since ST-00008; was DocumentManager.js)
//
// Compliance → Documents: upload certificates and reports and link each to one of the
// tenant's records. ST-00008: the record used to be a free-text "Entity ID" (an internal
// number nobody knows), and every failure was reported through react-toastify, whose
// container is not mounted anywhere in the app, so a refused upload looked like nothing
// happened. The record is now picked from a list, and every result is shown on the page.
import ThemedSelect from '../../common/ThemedSelect';
import { useState, useEffect, useCallback, useRef, type ChangeEvent, type DragEvent } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Upload,
  Download,
  Trash2,
  Search,
  File,
  Image,
  FileCheck,
  X,
} from 'lucide-react';
import { formatAppDate } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import type {
  ComplianceDocumentRow,
  ComplianceRequestError,
  DocumentEntityType,
  DocumentLinkTarget,
  DocumentListParams,
} from '../../../types/clientPages/weighbridgeCompliance';

// Values must match the server's linked_entity_type set (DOCUMENT_LINK_TARGETS in
// routes/weighbridgeCompliance.ts). 'Asset' is the weighbridge itself.
const ENTITY_TYPES: DocumentEntityType[] = [
  'Asset',
  'Calibration',
  'Verification',
  'Maintenance',
  'Operator',
  'NCR',
];
/** How a record of each kind is named in the form ("Select an asset…"). */
const ENTITY_NOUNS: Record<DocumentEntityType, string> = {
  Asset: 'asset',
  Calibration: 'calibration',
  Verification: 'verification',
  Maintenance: 'maintenance record',
  Operator: 'operator',
  NCR: 'NCR',
};
/** Where a missing record is added. */
const ENTITY_TABS: Record<DocumentEntityType, string> = {
  Asset: 'Assets',
  Calibration: 'Calibrations',
  Verification: 'Calibrations',
  Maintenance: 'Maintenance',
  Operator: 'Operators',
  NCR: 'NCR',
};
const DOCUMENT_TYPES = [
  'Verification Cert',
  'Calibration Cert',
  'Inspection Report',
  'Repair Report',
  'Photo',
  'Training Cert',
  'Insurance',
  'Other',
];
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const isEntityType = (value: unknown): value is DocumentEntityType =>
  typeof value === 'string' && (ENTITY_TYPES as string[]).includes(value);

const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (mimeType: string | null | undefined, fileName: string | null | undefined) => {
  if (mimeType?.startsWith('image/') || /\.(jpg|jpeg|png|gif)$/i.test(fileName || '')) {
    return <Image className="w-5 h-5 text-purple-500" />;
  }
  if (mimeType === 'application/pdf' || /\.pdf$/i.test(fileName || '')) {
    return <FileCheck className="w-5 h-5 text-red-500" />;
  }
  return <File className="w-5 h-5 text-blue-500" />;
};

/** The reason a request failed, in words a user can act on. */
const describeRequestError = (error: unknown, fallback: string): string => {
  const err = error as ComplianceRequestError | null | undefined;
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.error;
  // The permission gate answers a bare "Forbidden"; say what it means instead.
  if (serverMessage && serverMessage !== 'Forbidden') return serverMessage;
  if (status === 413) return 'The file is larger than the server accepts (25 MB at most).';
  if (status === 401) return 'Your session has expired. Sign in again, then retry.';
  if (status === 403) return 'You do not have permission to do this on the Compliance page.';
  if (!err?.response) return 'The server could not be reached. Check your connection and retry.';
  return fallback;
};

/** Keeps the accepted files; says which were left out and why. */
const acceptFiles = (files: File[]): { accepted: File[]; refused: string[] } => {
  const accepted: File[] = [];
  const refused: string[] = [];
  for (const f of files) {
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (ACCEPTED_EXTENSIONS.includes(ext) && f.size <= MAX_FILE_BYTES) accepted.push(f);
    else refused.push(f.name);
  }
  return { accepted, refused };
};

interface Notice {
  kind: 'success' | 'error';
  text: string;
}

export const DocumentManager = () => {
  const [documents, setDocuments] = useState<ComplianceDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState('All');
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  // Page-level result line (upload done, a refused drop, a failed download/delete).
  const [notice, setNotice] = useState<Notice | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadEntityType, setUploadEntityType] = useState<DocumentEntityType | ''>('');
  const [uploadEntityId, setUploadEntityId] = useState('');
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  // Why the upload in the panel was refused; stays until the next attempt.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [linkTargets, setLinkTargets] = useState<DocumentLinkTarget[]>([]);
  const [linkTargetsLoading, setLinkTargetsLoading] = useState(false);
  const [linkTargetsError, setLinkTargetsError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params: DocumentListParams = {};
      if (entityTypeFilter !== 'All') params.entityType = entityTypeFilter;
      if (docTypeFilter) params.documentType = docTypeFilter;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      const response = await api.documents.getAll(params);
      const body = response.data as
        { data?: ComplianceDocumentRow[] } | ComplianceDocumentRow[] | undefined;
      setDocuments(Array.isArray(body) ? body : body?.data || []);
      setListError(null);
    } catch (error) {
      setListError(describeRequestError(error, 'The documents could not be loaded.'));
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, docTypeFilter, debouncedSearchTerm]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // The records of the chosen kind, for the second field of the upload form.
  useEffect(() => {
    setUploadEntityId('');
    setLinkTargets([]);
    setLinkTargetsError(null);
    if (!uploadEntityType) return undefined;
    let current = true;
    setLinkTargetsLoading(true);
    api.documents
      .linkTargets(uploadEntityType)
      .then((response) => {
        if (current) setLinkTargets(response.data?.data || []);
      })
      .catch((error: unknown) => {
        if (current) {
          setLinkTargetsError(
            describeRequestError(
              error,
              `The ${ENTITY_NOUNS[uploadEntityType]} list could not be loaded.`
            )
          );
        }
      })
      .finally(() => {
        if (current) setLinkTargetsLoading(false);
      });
    return () => {
      current = false;
    };
  }, [uploadEntityType]);

  const chooseFiles = (files: File[]) => {
    const { accepted, refused } = acceptFiles(files);
    if (accepted.length === 0) {
      setNotice({
        kind: 'error',
        text: 'Only PDF, JPG and PNG files of up to 25 MB can be uploaded.',
      });
      return;
    }
    setNotice(
      refused.length > 0
        ? {
            kind: 'error',
            text: `Left out (not a PDF, JPG or PNG of up to 25 MB): ${refused.join(', ')}`,
          }
        : null
    );
    setUploadError(null);
    setUploadFiles(accepted);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    chooseFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    chooseFiles(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearUpload = () => {
    setUploadFiles([]);
    setUploadEntityType('');
    setUploadEntityId('');
    setUploadDocType('');
    setUploadProgress(0);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      setUploadError('No file is selected.');
      return;
    }
    if (!uploadEntityType || !uploadEntityId || !uploadDocType) {
      const missing = [
        !uploadEntityType && 'the entity type',
        uploadEntityType && !uploadEntityId && `the ${ENTITY_NOUNS[uploadEntityType]}`,
        !uploadDocType && 'the document type',
      ].filter(Boolean);
      setUploadError(`Choose ${missing.join(' and ')} first.`);
      return;
    }
    const linkedLabel =
      linkTargets.find((t) => String(t.id) === uploadEntityId)?.label || `#${uploadEntityId}`;
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setNotice(null);
    const uploaded: string[] = [];
    let failedAt = -1;
    let failure = '';
    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i]!;
      const formData = new FormData();
      formData.append('file', file);
      // Field names must be snake_case — the API speaks raw DB column names
      // end-to-end (see weighbridgeComplianceApi.ts).
      formData.append('linked_entity_type', uploadEntityType);
      formData.append('linked_entity_id', uploadEntityId);
      formData.append('document_type', uploadDocType);
      try {
        await api.documents.upload(formData);
        uploaded.push(file.name);
        setUploadProgress(Math.round(((i + 1) / uploadFiles.length) * 100));
      } catch (error) {
        console.error('Error uploading:', error);
        failedAt = i;
        failure = describeRequestError(error, 'The upload failed.');
        break;
      }
    }
    setUploading(false);

    if (failedAt === -1) {
      setNotice({
        kind: 'success',
        text: `Uploaded ${uploaded.length === 1 ? uploaded[0] : `${uploaded.length} files`} — linked to ${uploadEntityType} ${linkedLabel}.`,
      });
      clearUpload();
    } else {
      // Keep the failed file and the ones after it in the panel, with the reason.
      setUploadFiles(uploadFiles.slice(failedAt));
      setUploadProgress(0);
      setUploadError(
        `${uploadFiles[failedAt]!.name}: ${failure}` +
          (uploaded.length > 0 ? ` (${uploaded.join(', ')} uploaded.)` : '')
      );
    }
    if (uploaded.length > 0) await fetchDocuments();
  };

  const handleDownload = async (doc: ComplianceDocumentRow) => {
    try {
      const response = await api.documents.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_file_name || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setNotice({
        kind: 'error',
        text: `${doc.original_file_name || 'The document'} could not be downloaded: ${describeRequestError(error, 'the download failed.')}`,
      });
      console.error('Error downloading:', error);
    }
  };

  const handleDelete = async (id: number) => {
    const doc = documents.find((d) => d.id === id);
    try {
      await api.documents.delete(id);
      setNotice({ kind: 'success', text: `Deleted ${doc?.original_file_name || 'the document'}.` });
      setDeleteConfirm(null);
      fetchDocuments();
    } catch (error) {
      setDeleteConfirm(null);
      setNotice({
        kind: 'error',
        text: `${doc?.original_file_name || 'The document'} could not be deleted: ${describeRequestError(error, 'the delete failed.')}`,
      });
    }
  };

  const linkedEntityText = (doc: ComplianceDocumentRow): string | null => {
    if (doc.linked_entity_label) return doc.linked_entity_label;
    return doc.linked_entity_id ? `#${doc.linked_entity_id}` : null;
  };

  const getDocTypeBadge = (type: string | null) => {
    const colors: Record<string, string> = {
      'Verification Cert': 'bg-emerald-50 text-emerald-700',
      'Calibration Cert': 'bg-blue-50 text-blue-700',
      'Inspection Report': 'bg-indigo-50 text-indigo-700',
      'Repair Report': 'bg-amber-50 text-amber-700',
      Photo: 'bg-purple-50 text-purple-700',
      'Training Cert': 'bg-teal-50 text-teal-700',
      Insurance: 'bg-slate-100 text-slate-600',
      Other: 'bg-slate-100 text-slate-500',
    };
    if (!type) return <span className="text-sm text-slate-400">-</span>;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${colors[type] || colors.Other}`}
      >
        {type}
      </span>
    );
  };

  const SkeletonRow = () => (
    <tr>
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} className="px-5 py-3.5">
          <div
            className="h-4 bg-slate-200 rounded animate-pulse"
            style={{ width: `${50 + ((i * 37) % 50)}%` }}
          />
        </td>
      ))}
    </tr>
  );

  const SkeletonCard = () => (
    <div className="bg-surface rounded-xl border border-slate-200 p-4 space-y-3 animate-pulse">
      <div className="w-10 h-10 bg-slate-200 rounded-lg" />
      <div className="h-4 bg-slate-200 rounded w-3/4" />
      <div className="h-3 bg-slate-100 rounded w-1/2" />
      <div className="h-3 bg-slate-100 rounded w-1/3" />
    </div>
  );

  const noun = uploadEntityType ? ENTITY_NOUNS[uploadEntityType] : '';
  const recordPlaceholder = !uploadEntityType
    ? 'Choose the entity type first'
    : linkTargetsLoading
      ? 'Loading…'
      : linkTargets.length === 0
        ? `No ${noun} to choose`
        : `Select the ${noun}…`;

  const emptyState = (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
        <FileText className="w-8 h-8 text-slate-300" />
      </div>
      <h3 className="text-base font-semibold text-slate-700 mb-1">No documents found</h3>
      <p className="text-sm text-slate-400 max-w-sm">
        {searchTerm || entityTypeFilter !== 'All' || docTypeFilter
          ? 'Try adjusting your search or filter criteria'
          : 'Upload your first document using the drop zone above'}
      </p>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Document Manager
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Upload, manage and organise compliance documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${viewMode === 'table' ? 'bg-primary-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Table
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Grid
          </button>
        </div>
      </div>

      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer mb-6 ${
          dragOver
            ? 'border-primary-400 bg-primary-50/30'
            : 'border-slate-300 hover:border-primary-400 hover:bg-primary-50/30'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-600">Drop files here or click to upload</p>
        <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 25MB</p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          aria-label="Choose documents to upload"
          onChange={handleFileSelect}
        />
      </div>

      {notice && (
        <div
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={`flex items-start gap-2 rounded-lg border px-4 py-3 mb-6 text-sm ${
            notice.kind === 'error'
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-emerald-300 bg-emerald-50 text-emerald-800'
          }`}
        >
          {notice.kind === 'error' ? (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span className="flex-1">{notice.text}</span>
          <button
            onClick={() => setNotice(null)}
            aria-label="Dismiss message"
            className="p-0.5 rounded opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {uploadFiles.length > 0 && (
        <div className="bg-surface rounded-xl border border-slate-200 p-5 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Upload {uploadFiles.length} file(s)
            </h3>
            <button
              onClick={clearUpload}
              aria-label="Cancel upload"
              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2">
            {uploadFiles.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg"
              >
                {getFileIcon(f.type, f.name)}
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-xs text-slate-400">{formatFileSize(f.size)}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label
                htmlFor="wb-doc-entity-type"
                className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5"
              >
                Entity Type *
              </label>
              <ThemedSelect
                id="wb-doc-entity-type"
                value={uploadEntityType}
                onChange={(e) => {
                  const value = e.target.value;
                  setUploadEntityType(isEntityType(value) ? value : '');
                  setUploadError(null);
                }}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              >
                <option value="" disabled>
                  Select...
                </option>
                {ENTITY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            <div>
              <label
                htmlFor="wb-doc-entity-id"
                className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5"
              >
                {uploadEntityType || 'Linked record'} *
              </label>
              <ThemedSelect
                id="wb-doc-entity-id"
                value={uploadEntityId}
                disabled={!uploadEntityType || linkTargetsLoading || linkTargets.length === 0}
                onChange={(e) => {
                  setUploadEntityId(String(e.target.value));
                  setUploadError(null);
                }}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              >
                <option value="" disabled>
                  {recordPlaceholder}
                </option>
                {linkTargets.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.label}
                  </option>
                ))}
              </ThemedSelect>
              {linkTargetsError ? (
                <p className="text-xs text-red-700 mt-1">{linkTargetsError}</p>
              ) : uploadEntityType && !linkTargetsLoading && linkTargets.length === 0 ? (
                <p className="text-xs text-slate-500 mt-1">
                  There is no {noun} yet. Add one on the {ENTITY_TABS[uploadEntityType]} tab first.
                </p>
              ) : null}
            </div>
            <div>
              <label
                htmlFor="wb-doc-type"
                className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5"
              >
                Document Type *
              </label>
              <ThemedSelect
                id="wb-doc-type"
                value={uploadDocType}
                onChange={(e) => {
                  setUploadDocType(String(e.target.value));
                  setUploadError(null);
                }}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              >
                <option value="" disabled>
                  Select...
                </option>
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </div>

          {uploading && (
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {uploadError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-800"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold">Not uploaded. </span>
                {uploadError}
              </span>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleUpload}
              disabled={uploading}
              aria-busy={uploading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <Upload className="w-4 h-4" />
              {uploading ? `Uploading ${uploadProgress}%...` : 'Upload'}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by filename..."
            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
        </div>
        <div className="relative">
          <ThemedSelect
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(String(e.target.value))}
            aria-label="Filter by entity type"
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            {['All', ...ENTITY_TYPES].map((t) => (
              <option key={t} value={t}>
                {t === 'All' ? 'All Entities' : t}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="relative">
          <ThemedSelect
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(String(e.target.value))}
            aria-label="Filter by document type"
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Document Types</option>
            {DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </ThemedSelect>
        </div>
      </div>

      {listError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 mb-4 text-sm text-red-800"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="flex-1">The documents could not be loaded: {listError}</span>
          <button onClick={() => fetchDocuments()} className="font-semibold underline">
            Retry
          </button>
        </div>
      )}

      {viewMode === 'table' ? (
        <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  {[
                    'File Name',
                    'Document Type',
                    'Linked Entity',
                    'Upload Date',
                    'File Size',
                    'Uploaded By',
                    'Actions',
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan={7}>{emptyState}</td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-primary-50/30 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          {getFileIcon(doc.mime_type, doc.original_file_name)}
                          <span className="text-sm text-slate-700 font-medium truncate max-w-[200px]">
                            {doc.original_file_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">{getDocTypeBadge(doc.document_type)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {doc.linked_entity_type ? (
                          <span>
                            <span className="text-xs text-slate-400 uppercase">
                              {doc.linked_entity_type}
                            </span>
                            {linkedEntityText(doc) && (
                              <span className="ml-1 text-slate-700">{linkedEntityText(doc)}</span>
                            )}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {doc.uploaded_at ? formatAppDate(doc.uploaded_at) : '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {doc.uploaded_by || '-'}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(doc.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : documents.length === 0 ? (
            emptyState
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="bg-surface rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                      {getFileIcon(doc.mime_type, doc.original_file_name)}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        title="Download"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(doc.id)}
                        className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <h4
                    className="text-sm font-medium text-slate-700 truncate mb-1"
                    title={doc.original_file_name || undefined}
                  >
                    {doc.original_file_name}
                  </h4>
                  <div className="mb-2">{getDocTypeBadge(doc.document_type)}</div>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{doc.uploaded_at ? formatAppDate(doc.uploaded_at) : '-'}</span>
                    <span>{formatFileSize(doc.file_size)}</span>
                  </div>
                  {doc.linked_entity_type && (
                    <p className="text-[11px] text-slate-400 mt-1 truncate">
                      {doc.linked_entity_type}
                      {linkedEntityText(doc) ? ` - ${linkedEntityText(doc)}` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {deleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">Delete Document</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
